import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Target, BookOpen, ChevronRight, Loader2, ArrowLeft, ClipboardList, Trophy, Sparkles, BarChart3, Users, X, Clock, Repeat, Award, CheckCircle2, Circle, ArrowRight } from 'lucide-react';

// FASE QUIZZES · PASO 5A — Sección "🎯 Quizzes" del profesor.
// Lista de sólo consulta. Usa el endpoint existente GET /quizzes que ya
// filtra por teacher_id en el backend. Sin creación aquí — la creación sigue
// exclusivamente en Studies → StudyDetail → 🎯 Crear Quiz.

const TYPE_LABEL = { practice:'Práctica', evaluation:'Evaluación', module_exam:'Examen de módulo' };

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString(); } catch { return ''; }
}

export function QuizTeacherList() {
  const [items, setItems] = useState(null);
  const nav = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/admin/') ? '/admin' : '/teacher';
  useEffect(() => {
    api.get('/quizzes').then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);
  if (items === null) return <div className="min-h-[60vh] grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2"/> Cargando…</div>;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Header/>
        <div data-testid="quiz-teacher-empty" className="mt-8 rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center">
          <div className="text-5xl mb-3">🎯</div>
          <div className="font-display font-black text-xl text-slate-900">Aún no tienes Quizzes</div>
          <div className="text-sm text-slate-600 mt-1">Cuando crees un Quiz desde un Study, aparecerá aquí.</div>
          <Button data-testid="quiz-teacher-goto-studies" onClick={() => nav(`${basePath}/studies`)} className="mt-5 bg-indigo-600 hover:bg-indigo-700">
            <BookOpen className="w-4 h-4 mr-1"/> Ir a Studies
          </Button>
        </div>
      </div>
    );
  }

  const sorted = [...items].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-indigo-50/40 via-white to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Header/>
        <div data-testid="quiz-teacher-grid" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map(q => {
            const type = TYPE_LABEL[q.quiz_type] || 'Quiz';
            const nActs = (q.activity_snapshots || q.activity_ids || []).length;
            return (
              <div key={q.id} data-testid={`quiz-teacher-card-${q.id}`} className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 flex flex-col gap-3 hover:border-indigo-300 hover:shadow-md transition-all">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-500 text-white grid place-items-center shrink-0"><Target className="w-5 h-5"/></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-extrabold text-lg text-slate-900 truncate">{q.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1"><BookOpen className="w-3 h-3"/> {q.study_name || 'Study'}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1"><ClipboardList className="w-3 h-3"/> {type}</span>
                  <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3"/> {nActs} actividad{nActs===1?'':'es'}</span>
                  {q.module_slot ? <span className="inline-flex items-center gap-1"><Trophy className="w-3 h-3"/> Módulo {q.module_slot}</span> : <span/>}
                  {q.created_at ? <span className="text-slate-400">{fmtDate(q.created_at)}</span> : <span/>}
                </div>
                <Button data-testid={`quiz-teacher-view-${q.id}`} onClick={() => nav(`${basePath}/quizzes/${q.id}`)} variant="outline" className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                  Ver Quiz <ChevronRight className="w-4 h-4 ml-1"/>
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-start gap-3">
      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white grid place-items-center shadow-lg shadow-indigo-500/20">
        <Target className="w-6 h-6"/>
      </div>
      <div>
        <h1 data-testid="quiz-teacher-header" className="font-display text-3xl sm:text-4xl font-black text-slate-900 leading-tight">🎯 Mis Quizzes</h1>
        <p className="text-sm text-slate-600 mt-1">Consulta los quizzes que has creado para tus alumnos.</p>
      </div>
    </div>
  );
}

export function QuizTeacherDetail() {
  const { quizId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/admin/') ? '/admin' : '/teacher';
  const [q, setQ] = useState(null);
  const [err, setErr] = useState('');
  // FASE QUIZZES · PASO 5B — resumen de resultados
  // Reutilizamos el endpoint existente GET /quizzes/{quiz_id}/assignments.
  // null = cargando, [] = cargado sin asignaciones.
  const [assignments, setAssignments] = useState(null);
  useEffect(() => {
    api.get(`/quizzes/${quizId}`).then(r => setQ(r.data)).catch(e => setErr(e.response?.data?.detail || 'No se pudo cargar el Quiz'));
    api.get(`/quizzes/${quizId}/assignments`).then(r => setAssignments(Array.isArray(r.data) ? r.data : [])).catch(() => setAssignments([]));
  }, [quizId]);

  // FASE 5E · R3 — fuente única: `activity_snapshots`. El backend ya no
  // duplica `activities`. Cada snapshot expone `activity` (obj) y `position`,
  // que es exactamente lo que consume la vista.
  // FASE 5E · R4 — memoizamos el slice+sort para no recomputarlo en cada
  // render. Mismo criterio, mismo fallback, misma salida. DEBE declararse
  // ANTES de cualquier early return para no violar reglas de hooks; el
  // optional chaining `q?.activity_snapshots || []` cubre el caso `q=null`.
  const activities = useMemo(
    () => (q?.activity_snapshots || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0)),
    [q?.activity_snapshots]
  );

  if (err) return <div className="min-h-[60vh] grid place-items-center p-6"><div className="max-w-md rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center"><div className="text-4xl mb-2">😕</div><div className="font-display font-extrabold text-lg text-rose-800">No se pudo abrir el Quiz</div><div className="text-sm text-rose-700 mt-1">{err}</div><Button className="mt-4" onClick={()=>nav(`${basePath}/quizzes`)}><ArrowLeft className="w-4 h-4 mr-1"/> Volver</Button></div></div>;
  if (!q) return <div className="min-h-[60vh] grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2"/> Cargando…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <button onClick={()=>nav(`${basePath}/quizzes`)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4"/> Volver</button>
      <div data-testid="quiz-teacher-detail" className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white grid place-items-center shrink-0"><Target className="w-6 h-6"/></div>
          <div className="min-w-0">
            <div data-testid="qtd-title" className="font-display font-black text-2xl text-slate-900 truncate">{q.title}</div>
            <div className="text-sm text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5"/> {q.study_name || 'Study'}</span>
              <span>·</span>
              <span>{TYPE_LABEL[q.quiz_type] || 'Quiz'}</span>
              {q.module_slot ? <><span>·</span><span>Módulo {q.module_slot}</span></> : null}
              <span>·</span>
              <span>{activities.length} actividades</span>
            </div>
          </div>
        </div>
        {q.intro_text ? (
          <div className="mt-4 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-900 p-3 text-sm" data-testid="qtd-intro">
            <div className="text-[10px] uppercase tracking-widest text-indigo-600 font-bold mb-0.5">Introducción</div>
            {q.intro_text}
          </div>
        ) : null}
        <QuizResultsSummary assignments={assignments} totalActivities={activities.length} />
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Actividades</div>
          <ol data-testid="qtd-activities" className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
            {activities.map((a, i) => (
              <li key={a.activity_id || i} data-testid={`qtd-activity-${i}`} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                <span className="text-xs text-slate-400 w-8 shrink-0 font-mono">#{i + 1}</span>
                <span className="flex-1 truncate">{`Ejercicio ${i + 1}`}</span>
                <span className="text-[10px] uppercase text-slate-400 tracking-widest">{a.activity?.type || ''}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

// FASE QUIZZES · PASO 5B — Resumen de resultados del Quiz para el profesor.
// FASE QUIZZES · PASO 5E-2A — Añade chips por estado + lista individual de alumnos.
// Sólo frontend. Reutiliza los assignments cargados en QuizTeacherDetail.
// Cálculo del promedio: SOLO asignaciones con status === 'completed' usando
// final_score. La barra de progreso muestra N completed / M total (los que
// están in_progress o pending cuentan dentro de M pero NO en el promedio).
function QuizResultsSummary({ assignments, totalActivities = 0 }) {
  // FASE QUIZZES · PASO 5E-2B — detalle individual del alumno (on-demand).
  // Una sola llamada `GET /quiz-assignments/{id}` al seleccionar. La vista
  // principal (chips + lista) NO cambia y NO hace llamadas extra al cargar.
  const [selectedAsg, setSelectedAsg] = useState(null);

  if (assignments === null) {
    return (
      <div data-testid="qtd-results-loading" className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin"/> Cargando resultados…
      </div>
    );
  }

  const total = assignments.length;
  const completed = assignments.filter(a => a && a.status === 'completed');
  const inProgress = assignments.filter(a => a && a.status === 'in_progress');
  const pending = assignments.filter(a => a && a.status === 'pending');
  const nCompleted = completed.length;

  if (total === 0) {
    return (
      <div data-testid="qtd-results-empty-assignments" className="mt-5 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-5 text-center">
        <div className="w-10 h-10 mx-auto rounded-full bg-white border border-slate-200 grid place-items-center text-slate-400 mb-2"><Users className="w-5 h-5"/></div>
        <div className="font-display font-extrabold text-slate-800">Este Quiz todavía no tiene alumnos asignados.</div>
        <div className="text-xs text-slate-500 mt-1">Asigna el Quiz desde el Study para ver los resultados aquí.</div>
      </div>
    );
  }

  const pct = total > 0 ? Math.round((nCompleted / total) * 100) : 0;

  // Sólo promedio si al menos un alumno ha completado.
  let avgLabel = null;
  if (nCompleted > 0) {
    const sum = completed.reduce((acc, a) => acc + (Number(a.final_score) || 0), 0);
    const avg = sum / nCompleted;
    avgLabel = (Math.round(avg * 10) / 10).toFixed(1);
  }

  // Orden estable: completados primero (por final_score desc), luego in_progress
  // (por current_activity_position desc), luego pending. Alumnos sin nombre al final.
  const statusRank = { completed: 0, in_progress: 1, pending: 2 };
  const rows = [...assignments].sort((a, b) => {
    const ra = statusRank[a?.status] ?? 3;
    const rb = statusRank[b?.status] ?? 3;
    if (ra !== rb) return ra - rb;
    if (a?.status === 'completed') return (Number(b?.final_score) || 0) - (Number(a?.final_score) || 0);
    if (a?.status === 'in_progress') return (Number(b?.current_activity_position) || 0) - (Number(a?.current_activity_position) || 0);
    return (a?.student_name || '').localeCompare(b?.student_name || '');
  });

  return (
    <div data-testid="qtd-results-summary" className="mt-5 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/70 via-white to-white p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-indigo-500 text-white grid place-items-center shrink-0"><BarChart3 className="w-4 h-4"/></div>
        <div className="font-display font-black text-slate-900">📊 Resultados del Quiz</div>
      </div>

      {/* Chips de estado — resumen rápido */}
      <div data-testid="qtd-stats-chips" className="mb-4 flex flex-wrap gap-2">
        <span data-testid="qtd-stat-assigned" className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
          <Users className="w-3 h-3 text-slate-500"/> <span className="tabular-nums">{total}</span> asignado{total===1?'':'s'}
        </span>
        <span data-testid="qtd-stat-completed" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-800">
          🟢 <span className="tabular-nums">{completed.length}</span> completado{completed.length===1?'':'s'}
        </span>
        <span data-testid="qtd-stat-in-progress" className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-800">
          🟡 <span className="tabular-nums">{inProgress.length}</span> en progreso
        </span>
        <span data-testid="qtd-stat-pending" className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
          ⚪ <span className="tabular-nums">{pending.length}</span> pendiente{pending.length===1?'':'s'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {/* Nota media */}
        <div className="rounded-xl bg-white border border-slate-200 p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Nota media de la clase</div>
          {nCompleted === 0 ? (
            <div data-testid="qtd-results-no-completions" className="mt-1 text-sm text-slate-500">Aún no hay resultados.</div>
          ) : (
            <div className="mt-1 flex items-baseline flex-wrap gap-x-1.5 gap-y-0.5">
              <span data-testid="qtd-results-average" className="font-display font-black text-3xl sm:text-4xl text-indigo-700 tabular-nums">{avgLabel}</span>
              <span className="text-xs text-slate-500">/ 10</span>
              <span className="basis-full sm:basis-auto ml-0 sm:ml-1 text-[11px] text-slate-400">(sobre {nCompleted} completado{nCompleted===1?'':'s'})</span>
            </div>
          )}
        </div>

        {/* Progreso */}
        <div className="rounded-xl bg-white border border-slate-200 p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Progreso de la clase</div>
          <div className="mt-1 flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
            <span data-testid="qtd-results-progress-label" className="font-display font-black text-lg text-slate-900 tabular-nums">
              {nCompleted} / {total}
            </span>
            <span className="text-xs text-slate-500">alumno{total===1?'':'s'} completó{total===1?'':'aron'}</span>
          </div>
          <div className="mt-2 h-2.5 w-full rounded-full bg-slate-100 overflow-hidden" aria-label="Progreso de la clase">
            <div
              data-testid="qtd-results-progress-bar"
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-1 text-[11px] text-slate-400 tabular-nums">{pct}%</div>
        </div>
      </div>

      {/* Resultados de los alumnos — lista individual (5E-2A) */}
      <div data-testid="qtd-students-section" className="mt-5">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Resultados de los alumnos</div>

        {/* Desktop: tabla; Mobile: cards. Mismo dataset. */}
        <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
          {/* Cabecera visible sólo en ≥sm */}
          <div className="hidden sm:grid grid-cols-[1.5fr_1fr_0.9fr_0.6fr] gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold bg-slate-50 border-b border-slate-100">
            <div>Alumno</div>
            <div>Estado</div>
            <div>Progreso</div>
            <div className="text-right pr-1">Nota</div>
          </div>
          <ul data-testid="qtd-students-list" className="divide-y divide-slate-100">
            {rows.map(a => (
              <StudentRow key={a.id} a={a} totalActivities={totalActivities} onSelect={setSelectedAsg} />
            ))}
          </ul>
        </div>
      </div>

      {/* Detalle individual del alumno (5E-2B) — Dialog. Fetch on-demand. */}
      <StudentDetailDialog
        assignment={selectedAsg}
        totalActivities={totalActivities}
        onClose={() => setSelectedAsg(null)}
      />
    </div>
  );
}

// FASE 5E · R5 — memoizamos la fila para evitar re-renders de toda la lista
// cuando el diálogo de detalle abre/cierra (cambio de `selectedAsg` en el
// padre). Props: `a` (ref estable dentro de `assignments`), `totalActivities`
// (primitivo) y `onSelect` (setState estable). Comparación shallow por
// defecto es suficiente y segura.
const StudentRow = React.memo(function StudentRow({ a, totalActivities, onSelect }) {
  const status = a?.status || 'pending';
  const done = Math.max(0, Math.min(Number(a?.current_activity_position) || 0, totalActivities || 0));
  const label = status === 'completed' ? 'Completado' : status === 'in_progress' ? 'En progreso' : 'Pendiente';
  const icon = status === 'completed' ? '🟢' : status === 'in_progress' ? '🟡' : '⚪';
  const chipCls = status === 'completed'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : status === 'in_progress'
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-slate-50 text-slate-700 border-slate-200';
  const name = (a?.student_name || '').trim() || (a?.student_id || '').slice(0, 8) || 'Alumno';
  const scoreCell = status === 'completed' && Number.isFinite(Number(a?.final_score))
    ? <span className="font-display font-black text-slate-900 tabular-nums">{a.final_score}</span>
    : <span className="text-slate-400">—</span>;

  return (
    <li className="px-0 text-sm">
      <button
        type="button"
        onClick={() => onSelect?.(a)}
        aria-label={`Ver detalle de ${name}`}
        data-testid={`qtd-student-row-${a?.id || 'x'}`}
        className="w-full text-left px-3 py-2 sm:py-2.5 hover:bg-indigo-50/50 focus:bg-indigo-50/70 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors"
      >
        {/* Layout tabla (≥sm) */}
        <div className="hidden sm:grid grid-cols-[1.5fr_1fr_0.9fr_0.6fr_auto] gap-2 items-center">
          <div data-testid="qtd-student-name" className="font-semibold text-slate-900 truncate">{name}</div>
          <div>
            <span data-testid="qtd-student-status" className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${chipCls}`}>
              <span>{icon}</span> {label}
            </span>
          </div>
          <div data-testid="qtd-student-progress" className="tabular-nums text-slate-700">{done} / {totalActivities}</div>
          <div className="text-right pr-1" data-testid="qtd-student-score">{scoreCell}</div>
          <ChevronRight className="w-4 h-4 text-slate-300 shrink-0"/>
        </div>

        {/* Layout cards (<sm) */}
        <div className="sm:hidden">
          <div className="flex items-center justify-between gap-2">
            <div data-testid="qtd-student-name-m" className="font-semibold text-slate-900 truncate min-w-0">{name}</div>
            <div className="flex items-center gap-1 shrink-0">
              <span data-testid="qtd-student-score-m">{scoreCell}</span>
              <ChevronRight className="w-4 h-4 text-slate-300"/>
            </div>
          </div>
          <div className="mt-1 flex items-center justify-between gap-2 text-xs text-slate-600">
            <span data-testid="qtd-student-status-m" className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${chipCls}`}>
              <span>{icon}</span> {label}
            </span>
            <span data-testid="qtd-student-progress-m" className="tabular-nums">{done} / {totalActivities}</span>
          </div>
        </div>
      </button>
    </li>
  );
});

// FASE QUIZZES · PASO 5E-2B — Dialog de detalle individual (read-only).
// Hace UNA llamada bajo demanda a `GET /quiz-assignments/{id}` cuando se abre.
// Fuentes de verdad: assignment.quiz.activity_snapshots + assignment.progress[].
// NO consulta Studies, NO reproduce jugadas, NO muestra tableros.
function StudentDetailDialog({ assignment, totalActivities, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const asgId = assignment?.id || null;

  useEffect(() => {
    if (!asgId) { setDetail(null); setError(''); return; }
    let cancelled = false;
    setLoading(true); setError(''); setDetail(null);
    api.get(`/quiz-assignments/${asgId}`)
      .then(r => { if (!cancelled) setDetail(r.data); })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || 'No se pudo cargar el detalle'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [asgId]);

  const activitiesRow = useMemo(() => {
    if (!detail) return [];
    // FASE 5E · R3 — snapshots es la única fuente. Ya no existe fallback
    // a `detail.quiz.activities` (backend R3 eliminó ese campo del response).
    const snaps = detail?.quiz?.activity_snapshots || [];
    const list = snaps.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const progressByPos = new Map();
    (detail?.progress || []).forEach(p => { if (Number.isFinite(p?.position)) progressByPos.set(Number(p.position), p); });
    const cur = Number(detail?.current_activity_position || 0);
    // 5E-2B: un alumno `pending` (sin ningún progreso) NO tiene "actividad
    // actual" — todas se muestran como pendientes. Solo `in_progress` marca
    // la current según current_activity_position.
    const assignmentStatus = detail?.status || 'pending';
    return list.map((entry, idx) => {
      const position = Number(entry?.position ?? idx);
      const actMeta = entry?.activity || entry || {};
      const prog = progressByPos.get(position);
      const isCompleted = prog?.status === 'completed';
      const isCurrent = !isCompleted && assignmentStatus === 'in_progress' && position === cur;
      return {
        position,
        title: `Ejercicio ${position + 1}`,
        type: actMeta?.type || '',
        status: isCompleted ? 'completed' : isCurrent ? 'current' : 'pending',
        points_earned: prog?.points_earned ?? null,
        attempts_used: prog?.attempts_used ?? null,
        elapsed_seconds: prog?.elapsed_seconds ?? null,
      };
    });
  }, [detail]);

  const open = !!assignment;
  const name = (assignment?.student_name || '').trim() || (assignment?.student_id || '').slice(0, 8) || 'Alumno';
  const status = detail?.status || assignment?.status || 'pending';
  const statusLabel = status === 'completed' ? 'Completado' : status === 'in_progress' ? 'En progreso' : 'Pendiente';
  const statusIcon = status === 'completed' ? '🟢' : status === 'in_progress' ? '🟡' : '⚪';
  const statusCls = status === 'completed'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : status === 'in_progress'
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-slate-50 text-slate-700 border-slate-200';
  const done = Math.max(0, Math.min(Number(detail?.current_activity_position ?? assignment?.current_activity_position ?? 0), totalActivities || 0));
  const points = Number(detail?.points_obtained ?? assignment?.points_obtained ?? 0);
  const maxPts = Number(detail?.max_points ?? assignment?.max_points ?? 0);
  const finalScore = detail?.final_score ?? assignment?.final_score;
  const startedAt = detail?.started_at ?? assignment?.started_at;
  const completedAt = detail?.completed_at ?? assignment?.completed_at;
  const quizTitle = detail?.quiz?.title || '';

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="qtd-student-detail" className="max-w-2xl w-[95vw] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-start gap-3 font-display text-xl text-slate-900">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white grid place-items-center shrink-0"><Users className="w-5 h-5"/></div>
            <div className="min-w-0 flex-1">
              <div data-testid="qtd-detail-name" className="font-black text-lg sm:text-xl truncate">{name}</div>
              {quizTitle ? <div data-testid="qtd-detail-quiz-title" className="text-xs sm:text-sm text-slate-500 font-normal truncate">{quizTitle}</div> : null}
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">Detalle individual del alumno para este Quiz.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[75vh] overflow-y-auto overflow-x-hidden px-4 sm:px-5 py-4">
          {loading && (
            <div data-testid="qtd-detail-loading" className="py-8 grid place-items-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mb-2"/>
              <div className="text-sm">Cargando detalle…</div>
            </div>
          )}
          {!loading && error && (
            <div data-testid="qtd-detail-error" className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-4 text-sm">
              <div className="font-semibold mb-1">No pudimos cargar el detalle</div>
              <div className="text-xs">{error}</div>
            </div>
          )}
          {!loading && !error && open && (
            <div className="space-y-4">
              {/* Resumen del alumno */}
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span data-testid="qtd-detail-status" className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-semibold ${statusCls}`}>
                    <span>{statusIcon}</span> {statusLabel}
                  </span>
                  <span data-testid="qtd-detail-progress" className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 px-2.5 py-1 text-[12px] font-semibold text-slate-700 tabular-nums">
                    <BarChart3 className="w-3 h-3"/> {done} / {totalActivities} actividades
                  </span>
                  {maxPts > 0 && (
                    <span data-testid="qtd-detail-points" className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2.5 py-1 text-[12px] font-semibold text-indigo-800 tabular-nums">
                      <Sparkles className="w-3 h-3"/> {points} / {maxPts} puntos
                    </span>
                  )}
                  {status === 'completed' && Number.isFinite(Number(finalScore)) && (
                    <span data-testid="qtd-detail-final-score" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[12px] font-black text-emerald-800 tabular-nums">
                      <Trophy className="w-3 h-3"/> Nota {finalScore} / 10
                    </span>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px] text-slate-600">
                  <div data-testid="qtd-detail-started-at" className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-400"/> Inicio: <span className="text-slate-800">{fmtDateTime(startedAt) || '—'}</span>
                  </div>
                  <div data-testid="qtd-detail-completed-at" className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-slate-400"/> Finalización: <span className="text-slate-800">{fmtDateTime(completedAt) || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Desglose de actividades */}
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Actividades</div>
                <ol data-testid="qtd-detail-activities" className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden bg-white">
                  {activitiesRow.length === 0 && (
                    <li className="px-3 py-6 text-center text-sm text-slate-400">Este Quiz no tiene actividades.</li>
                  )}
                  {activitiesRow.map(row => (
                    <li
                      key={row.position}
                      data-testid={`qtd-detail-activity-${row.position}`}
                      data-status={row.status}
                      className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2.5 text-sm ${row.status === 'current' ? 'bg-amber-50/40' : ''}`}
                    >
                      <span className="text-xs text-slate-400 w-6 sm:w-8 shrink-0 font-mono tabular-nums">#{row.position + 1}</span>
                      <span className="w-5 shrink-0 grid place-items-center" aria-hidden>
                        {row.status === 'completed' ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/>
                          : row.status === 'current' ? <ArrowRight className="w-4 h-4 text-amber-600"/>
                          : <Circle className="w-4 h-4 text-slate-300"/>}
                      </span>
                      <span className="flex-1 min-w-0 truncate text-slate-800">{row.title}</span>
                      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 text-[11px] text-slate-500 tabular-nums">
                        {row.attempts_used != null && row.status === 'completed' && (
                          <span data-testid={`qtd-detail-attempts-${row.position}`} className="inline-flex items-center gap-0.5" title="Intentos utilizados">
                            <Repeat className="w-3 h-3"/> {row.attempts_used}
                          </span>
                        )}
                        {row.elapsed_seconds != null && row.elapsed_seconds > 0 && row.status === 'completed' && (
                          <span data-testid={`qtd-detail-elapsed-${row.position}`} className="inline-flex items-center gap-0.5" title="Tiempo empleado">
                            <Clock className="w-3 h-3"/> {fmtElapsed(row.elapsed_seconds)}
                          </span>
                        )}
                        <span data-testid={`qtd-detail-points-${row.position}`} className="font-semibold text-slate-700 min-w-[42px] text-right">
                          {row.status === 'completed' && row.points_earned != null
                            ? <><Award className="w-3 h-3 inline mr-0.5 text-indigo-500"/>{row.points_earned}</>
                            : <span className="text-slate-300">—</span>}
                        </span>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t border-slate-100 bg-slate-50/50">
          <Button data-testid="qtd-detail-close" variant="outline" onClick={() => onClose?.()} className="w-full sm:w-auto">
            <X className="w-4 h-4 mr-1"/> Volver a resultados
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtDateTime(s) {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}
function fmtElapsed(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}min` : `${m}:${String(r).padStart(2,'0')}`;
}

export default QuizTeacherList;
